// MyOrganization — page for ORG_ADMINs to manage their tenant.
// Shows current org details, member list, and lets them invite new members.
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Building2, Mail, Plus, Loader2, Copy, Check, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ROLE_LABEL = {
  SUPERADMIN: 'Superadmin',
  ORG_ADMIN: 'Org Admin',
  ORG_USER: 'Member',
  PLATFORM_ADMIN: 'Org Admin',
  USER: 'Member',
};

export default function MyOrganization() {
  const { user } = useAuth();
  const isOrgAdmin = user?.platformRole === 'ORG_ADMIN' || user?.platformRole === 'SUPERADMIN' || user?.role === 'admin';

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showInvite, setShowInvite] = useState(false);
  const [invitee, setInvitee] = useState({ email: '', firstName: '', lastName: '', role: 'ORG_USER' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const orgId = user?.organizationId;

  const load = async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [o, m, i] = await Promise.all([
        api.get(`/orgs/${orgId}`),
        api.get(`/orgs/${orgId}/members`),
        api.get('/invitations'),
      ]);
      setOrg(o.data);
      setMembers(m.data?.members || []);
      setInvites((i.data?.invitations || []).filter(x => x.status === 'PENDING'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load organization');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orgId]);

  const sendInvite = async (e) => {
    e?.preventDefault();
    if (!invitee.email) { toast.error('Email is required'); return; }
    setInviting(true); setInviteResult(null);
    try {
      const r = await api.post(`/orgs/${orgId}/invite-user`, invitee);
      setInviteResult(r.data);
      toast.success('Invitation sent');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation');
    } finally { setInviting(false); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invitation for ${inv.email}?`)) return;
    try { await api.delete(`/invitations/${inv.invite_id}`); toast.success('Invitation revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const closeInvite = () => {
    setShowInvite(false);
    setInvitee({ email: '', firstName: '', lastName: '', role: 'ORG_USER' });
    setInviteResult(null); setCopied(false);
  };

  if (!isOrgAdmin) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto py-12 text-center" data-testid="org-restricted">
          <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-amber-500"/>
          <h2 className="text-lg text-slate-100 mb-1">Organization admin required</h2>
          <p className="text-sm text-slate-400">Only Organization Admins can view and manage tenant members.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-5 h-5" style={{ color: '#5790C9' }}/>
              <h1 className="text-2xl font-light" style={{ color: 'var(--text-primary)' }}>My Organization</h1>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {org?.name || 'Organization'} · {org?.industry || '—'} · {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <Button onClick={() => setShowInvite(true)} data-testid="btn-invite-member" className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px]">
            <Plus className="w-4 h-4 mr-1.5"/> Invite Member
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
        ) : (
          <>
            <SectionHeading>Members</SectionHeading>
            <div className="rounded-xl overflow-hidden mb-8" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
                  <tr>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Role</th>
                    <th className="text-left px-5 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-12 text-slate-500">No members yet. Invite someone to get started.</td></tr>
                  ) : members.map(m => (
                    <tr key={m.user_id} data-testid={`member-row-${m.user_id}`} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <td className="px-5 py-3" style={{ color: 'var(--text-primary)' }}>{m.fullName || m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—'}</td>
                      <td className="px-5 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{m.email}</td>
                      <td className="px-5 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border border-slate-700 text-slate-300 bg-slate-800/50">{ROLE_LABEL[m.platformRole] || m.platformRole || 'Member'}</span></td>
                      <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{(m.createdAt || '').slice(0, 10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <SectionHeading>Pending Invitations <span className="text-[10px] tracking-[0.2em] text-slate-500 ml-2">{invites.length}</span></SectionHeading>
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
                  <tr>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Role</th>
                    <th className="text-left px-5 py-3">Invited by</th>
                    <th className="text-left px-5 py-3">Expires</th>
                    <th className="text-right px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-12 text-slate-500">No pending invitations.</td></tr>
                  ) : invites.map(i => (
                    <tr key={i.invite_id} data-testid={`invite-row-${i.invite_id}`} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <td className="px-5 py-3 font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{i.email}</td>
                      <td className="px-5 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border border-amber-500/40 text-amber-400 bg-amber-500/10">{ROLE_LABEL[i.role] || i.role}</span></td>
                      <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{i.invitedByEmail || '—'}</td>
                      <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{(i.expiresAt || '').slice(0,10)}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => revokeInvite(i)} className="text-[11px] tracking-wider uppercase text-red-400 hover:text-red-300" data-testid={`btn-revoke-${i.invite_id}`}>Revoke</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={showInvite} onOpenChange={(o) => !o && closeInvite()}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 sm:max-w-md" data-testid="dialog-invite-member">
          <DialogHeader>
            <DialogTitle>Invite a member</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Send an emailed invitation. The invitee will set their own password on activation.
            </DialogDescription>
          </DialogHeader>
          {!inviteResult ? (
            <form onSubmit={sendInvite} className="space-y-3">
              <div>
                <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Email *</Label>
                <Input type="email" required value={invitee.email} onChange={e => setInvitee(v => ({...v, email: e.target.value}))} data-testid="invite-input-email" className="bg-slate-900 border-slate-800"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">First name</Label>
                  <Input value={invitee.firstName} onChange={e => setInvitee(v => ({...v, firstName: e.target.value}))} data-testid="invite-input-fn" className="bg-slate-900 border-slate-800"/>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Last name</Label>
                  <Input value={invitee.lastName} onChange={e => setInvitee(v => ({...v, lastName: e.target.value}))} data-testid="invite-input-ln" className="bg-slate-900 border-slate-800"/>
                </div>
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Role</Label>
                <select value={invitee.role} onChange={e => setInvitee(v => ({...v, role: e.target.value}))} data-testid="invite-input-role"
                  className="w-full px-3 py-2 rounded-md text-sm bg-slate-900 border border-slate-800 text-slate-100">
                  <option value="ORG_USER">Member</option>
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Only the superadmin can promote a user to Org Admin.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeInvite} className="border-slate-700 text-slate-300">Cancel</Button>
                <Button type="submit" disabled={inviting} data-testid="invite-submit-btn" className="bg-[#F26B1F] hover:bg-[#d55812] text-white tracking-[0.15em] uppercase text-[12px]">
                  {inviting ? 'Sending…' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md p-3 border border-emerald-500/40 bg-emerald-500/5">
                <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-400 mb-1">Invitation created</div>
                <p className="text-xs text-slate-300">Email queued via Resend. Share the link below if needed.</p>
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Invite link</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={inviteResult.invite_link} data-testid="invite-link" className="bg-slate-900 border-slate-800 text-slate-200 text-xs font-mono"/>
                  <button onClick={() => copyLink(inviteResult.invite_link)} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded border border-slate-700 hover:border-slate-500 text-slate-300 text-xs">
                    {copied ? <><Check className="w-3.5 h-3.5"/> Copied</> : <><Copy className="w-3.5 h-3.5"/> Copy</>}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Expires {inviteResult.invitation?.expiresAt?.slice(0,10)}.</p>
              </div>
              <DialogFooter>
                <Button onClick={closeInvite} className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px]">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-[10px] tracking-[0.3em] uppercase font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
      <Mail className="w-3.5 h-3.5"/> {children}
    </div>
  );
}
